import React, { useMemo, useState } from 'react';
import { Button, Skeleton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@lognai/synapse-design-system-v2';
import { useInsights } from '../../hooks/use-insights';
import { InsightRow } from './InsightRow';
import { getRegisteredPluginIds } from '../../utils/entity-route-for';
import type { InsightSeverity, InsightStatus } from '../../api/insights';

const humanizePluginId = (id: string): string =>
  id.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export interface InsightsListProps {
  status: InsightStatus;
  onNavigate?: (url: string) => void;
}

export const InsightsList: React.FC<InsightsListProps> = ({ status, onNavigate }) => {
  const [severity, setSeverity] = useState<InsightSeverity | 'all'>('all');
  const [pluginId, setPluginId] = useState<string>('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = {
    status,
    ...(severity !== 'all' ? { severity } : {}),
    ...(pluginId !== 'all' ? { pluginId } : {}),
    cursor,
    limit: 20,
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useInsights(query);

  // Plugin dropdown options derive from whichever UI plugins actually
  // registered entity routes (populated at app startup). Adding a new
  // *-ui plugin surfaces in the filter automatically.
  const pluginOptions = useMemo(() => getRegisteredPluginIds(), []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Severity</label>
          <Select value={severity} onValueChange={(v) => { setSeverity(v as InsightSeverity | 'all'); setCursor(undefined); }}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Plugin</label>
          <Select value={pluginId} onValueChange={(v) => { setPluginId(v); setCursor(undefined); }}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {pluginOptions.map((id) => (
                <SelectItem key={id} value={id}>{humanizePluginId(id)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : isError ? (
        /*
          A failed load has to look like a failed load.

          This used to read only `data` and `isLoading`, which gave the failure
          two ways to lie. The query refetches on an interval and stays
          `pending` while it never succeeds, so a persistent error (429s on
          staging) left three skeleton rows cycling forever with no message and
          nothing to click. And when it did settle, `!data` fell through to the
          empty state below — telling the user there are no insights, which is
          a different and worse claim than "we could not load them".

          Says which it is, and offers the retry, the way the Analytics
          dashboard already does for the same class of failure
          (QA ledger GI2-001).
        */
        <div
          role="alert"
          className="rounded-lg border border-destructive-border bg-destructive-subtle p-6 text-center"
        >
          <div className="text-sm font-medium text-destructive-foreground">
            Insights could not be loaded
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {error instanceof Error && error.message
              ? error.message
              : 'The request did not complete. This is usually temporary.'}
          </div>
          <Button className="mt-4" variant="outline" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <div className="text-base mb-1">No {status} insights</div>
          <div className="text-sm">The engine surfaces signals like late shipments and overdue POs here.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((insight) => (
            <InsightRow key={insight.id} insight={insight} onNavigate={onNavigate} />
          ))}
          {data.nextCursor && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setCursor(data.nextCursor ?? undefined)}>
                Next page →
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
